import { FC, useContext, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { useCarPurchaseDocumentsApi } from "../../http/carPurchaseDocuments/api";
import { CarPurchaseDocument, RenameCarPurchaseDocument } from "../../http/carPurchaseDocuments/types";
import { isConflict } from "../../http/errors";
import { PaginationQuery } from "../../types";
import { formatTimeStamp, isEmbed, isImage, isMobile, isVideo } from "../../types/utils";
import SvgEdit from "../icons/Edit";
import DownloadLink from "../ui/DownloadLink";
import FileIcon from "../ui/FileIcon";
import { Action, getDeleteAction, getDownloadAction, getPreviewAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import PreviewFileDialog from "../ui/PreviewFileDialog";
import Table from "../ui/Table";
import { FileToOverwrite, type TableUploadFileResult } from "../ui/TableFileUploadDialog";
import DeleteCarPurchaseDocumentDialog from "./DeleteCarPurchaseDocumentDialog";
import UploadCarPurchaseDocument from "./UploadCarPurchaseDocument";

type CarPurchaseDocumentsProps = {
  setIsDragging: (isDragging: boolean) => void;
};

const CarPurchaseDocuments: FC<CarPurchaseDocumentsProps> = ({ setIsDragging }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const carDocumentsApi = useCarPurchaseDocumentsApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const [itemToDelete, setItemToDelete] = useState<CarPurchaseDocument>();
  const [itemToPreview, setItemToPreview] = useState<{ name: string; url: string }>();
  const [documentIdToRename, setDocumentIdToRename] = useState<string>("");
  const [fileToOverwrite, setFileToOverwrite] = useState<FileToOverwrite>();
  const [isOverwritingFile, setIsOverwritingFile] = useState(false);
  const { id: carPurchaseId } = useParams<{ id: string }>();
  const api = useApi();
  const canUploadDocuments = !!carPurchaseId && userSettings.permissions.carPurchases.insert;

  const onSubmit = async (id: string, name: string, callback: () => void): Promise<void> => {
    const body: RenameCarPurchaseDocument = {
      name,
    };

    try {
      await carDocumentsApi.renameDocument(id, body, carPurchaseId);
      callback();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const result = carDocumentsApi.useCarPurchaseDocuments(query, aborter.current.signal, carPurchaseId);
  const [data] = useDataInitializer(result.data, result.error);

  const uploadCarPurchaseDocument = async (file: File): Promise<TableUploadFileResult> => {
    if (!carPurchaseId) {
      return "skipped";
    }

    try {
      await carDocumentsApi.insertCarPurchaseDocument(file, false, carPurchaseId);
    } catch (error) {
      if (!isConflict(error)) {
        throw error;
      }

      return new Promise<TableUploadFileResult>((resolve, reject) => {
        setFileToOverwrite({
          file,
          onCancel: () => {
            setFileToOverwrite(undefined);
            resolve("skipped");
          },
          onOverwrite: () => {
            setIsOverwritingFile(true);
            carDocumentsApi
              .insertCarPurchaseDocument(file, true, carPurchaseId)
              .then(() => {
                setFileToOverwrite(undefined);
                resolve("success");
              })
              .catch((error) => {
                setFileToOverwrite(undefined);
                reject(error);
              })
              .finally(() => setIsOverwritingFile(false));
          },
        });
      });
    }
  };

  const downloadDocument = (item: CarPurchaseDocument) =>
    api
      .downloadFile(`/api/admin/car_purchase_documents/${item.id}` + `?t=${new Date().getTime()}`, item.name)
      .catch((err) => dispatch(showErrorNotification(i18n, err)));

  const onPreviewClick = async (item: CarPurchaseDocument) => {
    if (!isMobile && (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))) {
      const docUrl = await api.createObjectURL(
        `/api/admin/car_purchase_documents/${item.id}` + `?t=${new Date().getTime()}`,
        true,
      );
      setItemToPreview({ name: item.name, url: docUrl });
      return;
    }
    downloadDocument(item);
  };

  const getRowActions = (item: CarPurchaseDocument) => {
    const actions: Action[] = [];

    if (userSettings.permissions.carPurchases.read) {
      actions.push(getDownloadAction(i18n, () => downloadDocument(item)));
    }

    if (
      userSettings.permissions.carPurchases.read &&
      !isMobile &&
      (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))
    ) {
      actions.push(getPreviewAction(i18n, () => onPreviewClick(item)));
    }

    if (userSettings.permissions.carPurchases.update) {
      actions.push({
        title: i18n.t.common.rename,
        onClick: () => setDocumentIdToRename(item.id),
        icon: SvgEdit,
      });
    }

    if (userSettings.permissions.carPurchases.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  return (
    <>
      <Table<CarPurchaseDocument>
        filters={filters}
        fileDrop={{
          enabled: canUploadDocuments,
          onUploadFile: uploadCarPurchaseDocument,
          isDraggingOn: setIsDragging,
        }}
        buttons={{
          main: canUploadDocuments && <UploadCarPurchaseDocument carPurchaseId={carPurchaseId!} />,
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <>
            <th align="left" style={{ width: 1 }}></th>
            <th align="left">{i18n.t.common.name}</th>
            <th align="left">{i18n.t.common.uploadedBy}</th>
            <th align="left">{i18n.t.common.uploadedAt}</th>
            <th align="left">{i18n.t.common.modifiedBy}</th>
            <th align="left">{i18n.t.common.modifiedAt}</th>
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            <Table.IconTd>
              <FileIcon fileName={item.name} />
            </Table.IconTd>
            <Table.EditableCell
              defaultName={item.name}
              itemId={item.id}
              onSubmit={onSubmit}
              onCancel={() => setDocumentIdToRename("")}
              isEditing={item.id === documentIdToRename}
              disabled={!userSettings.permissions.carPurchases.update}
            >
              <DownloadLink url={`/api/admin/car_purchase_documents/${item.id}`} fileName={item.name}>
                {item.name}
              </DownloadLink>
            </Table.EditableCell>
            <td>{item.uploader.fullName}</td>
            <td>{formatTimeStamp(item.uploadedAt, i18n.language)}</td>
            <td>{item.editor?.fullName ?? ""}</td>
            <td>{!!item.editor ? formatTimeStamp(item.modifiedAt, i18n.language) : ""}</td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!fileToOverwrite && (
        <ModalDialog
          title={i18n.t.documents.alreadyPresent}
          isSubmitting={isOverwritingFile}
          onClose={() => {
            if (!isOverwritingFile) {
              fileToOverwrite.onCancel();
            }
          }}
          actions={[
            {
              title: i18n.t.common.cancel,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onCancel,
              type: "secondary",
            },
            {
              title: i18n.t.common.overwrite,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onOverwrite,
            },
          ]}
        >
          <p>{fileToOverwrite.file.name}</p>
          {i18n.t.documents.questions.overwrite}
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <DeleteCarPurchaseDocumentDialog
          carPurchaseDocument={itemToDelete}
          onClose={() => setItemToDelete(undefined)}
        />
      )}

      {!!itemToPreview && (
        <PreviewFileDialog
          fileName={itemToPreview.name}
          onClose={() => setItemToPreview(undefined)}
          docUrl={itemToPreview.url}
        />
      )}
    </>
  );
};

export default CarPurchaseDocuments;
